import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  User, Mail, Phone, Camera, Upload, Trash2, Save, 
  GraduationCap, Briefcase, RefreshCw, Check
} from 'lucide-react';
import { toast } from 'sonner';
import ChangePasswordCard from '../../components/ChangePasswordCard';
import { resolveUploadUrl } from '../../utils/media';

const TrainerProfilePage = ({ onMenuClick }) => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);
  
  // Profile data
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    specialization: '',
    bio: ''
  });
  
  // Local avatar state for immediate update
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url || null);
  const [avatarBroken, setAvatarBroken] = useState(false);
  const [avatarCacheKey, setAvatarCacheKey] = useState(Date.now());

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      // Get trainer profile
      const response = await api.get('/trainer/profile');
      setProfile({
        first_name: response.data.first_name || user?.first_name || '',
        last_name: response.data.last_name || user?.last_name || '',
        email: response.data.email || user?.email || '',
        phone: response.data.phone || '',
        specialization: response.data.specialization || '',
        bio: response.data.bio || ''
      });
      setAvatarUrl(response.data.avatar_url || user?.avatar_url || null);
    } catch (error) {
      // If endpoint doesn't exist, use user data
      setProfile({
        first_name: user?.first_name || '',
        last_name: user?.last_name || '',
        email: user?.email || '',
        phone: '',
        specialization: '',
        bio: ''
      });
      setAvatarUrl(user?.avatar_url || null);
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Type de fichier non autorisé. Utilisez JPG, PNG, GIF ou WebP.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux. Maximum 5 Mo.');
      return;
    }

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/auth/upload-avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setAvatarUrl(response.data.avatar_url);
      setAvatarBroken(false);
      setAvatarCacheKey(Date.now());
      
      // Update user context
      if (updateUser) {
        updateUser({ ...user, avatar_url: response.data.avatar_url });
      }
      
      toast.success('Photo de profil mise à jour ! Elle sera visible partout dans la plateforme.');
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error("Erreur lors de l'upload de la photo");
    } finally {
      setUploadingAvatar(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteAvatar = async () => {
    if (!avatarUrl) return;
    
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer votre photo de profil ?')) {
      return;
    }

    try {
      await api.delete('/auth/avatar');
      setAvatarUrl(null);
      setAvatarBroken(false);
      
      // Update user context
      if (updateUser) {
        updateUser({ ...user, avatar_url: null });
      }
      
      toast.success('Photo de profil supprimée');
    } catch (error) {
      console.error('Error deleting avatar:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/trainer/profile', {
        phone: profile.phone,
        specialization: profile.specialization,
        bio: profile.bio
      });
      toast.success('Profil mis à jour avec succès');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const getInitials = () => {
    const first = profile.first_name?.[0] || '';
    const last = profile.last_name?.[0] || '';
    return (first + last).toUpperCase() || 'FT';
  };

  const getAvatarSrc = () => {
    const src = resolveUploadUrl(avatarUrl);
    if (!src) return null;
    const sep = src.includes('?') ? '&' : '?';
    return `${src}${sep}t=${avatarCacheKey}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
        <Header title="Mon Profil" onMenuClick={onMenuClick} />
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Mon Profil" onMenuClick={onMenuClick} />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header Card with Photo */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row items-center gap-6">
              {/* Avatar */}
              <div className="relative group">
                <div 
                  className="w-32 h-32 rounded-full overflow-hidden cursor-pointer ring-4 ring-emerald-100 dark:ring-emerald-900"
                  onClick={handleAvatarClick}
                >
                  {avatarUrl && !avatarBroken ? (
                    <img 
                      src={getAvatarSrc()} 
                      alt="" 
                      className="w-full h-full object-cover"
                      onError={() => setAvatarBroken(true)}
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-emerald-600 to-teal-600 flex items-center justify-center">
                      <span className="text-white text-4xl font-bold">
                        {getInitials()}
                      </span>
                    </div>
                  )}
                  
                  {/* Overlay on hover */}
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    {uploadingAvatar ? (
                      <RefreshCw className="w-8 h-8 text-white animate-spin" />
                    ) : (
                      <Camera className="w-8 h-8 text-white" />
                    )}
                  </div>
                </div>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleAvatarUpload}
                  className="hidden"
                />
                
                {/* Photo actions */}
                <div className="flex justify-center gap-2 mt-3">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleAvatarClick}
                    disabled={uploadingAvatar}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    {avatarUrl ? 'Modifier' : 'Ajouter'}
                  </Button>
                  {avatarUrl && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleDeleteAvatar}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Name and Role */}
              <div className="text-center md:text-left flex-1">
                <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {profile.first_name} {profile.last_name}
                </h1>
                <div className="flex items-center justify-center md:justify-start gap-2 mt-2">
                  <GraduationCap className="w-5 h-5 text-emerald-600" />
                  <span className="text-emerald-600 font-medium">Formateur</span>
                </div>
                {profile.specialization && (
                  <div className="flex items-center justify-center md:justify-start gap-2 mt-1">
                    <Briefcase className="w-4 h-4 text-slate-500" />
                    <span className="text-slate-600 dark:text-slate-400">{profile.specialization}</span>
                  </div>
                )}
                <p className="text-slate-500 text-sm mt-2">
                  Votre photo sera visible par les administrateurs et les étudiants de vos cours.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-emerald-600" />
              Informations du profil
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Identity (read-only) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-600">Prénom</Label>
                <Input 
                  value={profile.first_name} 
                  disabled 
                  className="bg-slate-50 mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Contactez l'administrateur pour modifier</p>
              </div>
              <div>
                <Label className="text-slate-600">Nom</Label>
                <Input 
                  value={profile.last_name} 
                  disabled 
                  className="bg-slate-50 mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-600 flex items-center gap-2">
                <Mail className="w-4 h-4" /> Email
              </Label>
              <Input 
                value={profile.email} 
                disabled 
                className="bg-slate-50 mt-1"
              />
            </div>

            {/* Editable fields */}
            <div>
              <Label className="text-slate-700 flex items-center gap-2">
                <Phone className="w-4 h-4" /> Téléphone
              </Label>
              <Input 
                value={profile.phone}
                onChange={(e) => setProfile({...profile, phone: e.target.value})}
                placeholder="06 12 34 56 78"
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-700 flex items-center gap-2">
                <Briefcase className="w-4 h-4" /> Spécialité / Domaine d'expertise
              </Label>
              <Input 
                value={profile.specialization}
                onChange={(e) => setProfile({...profile, specialization: e.target.value})}
                placeholder="ex: Marketing Digital, Développement Web..."
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-slate-700">Biographie</Label>
              <Textarea 
                value={profile.bio}
                onChange={(e) => setProfile({...profile, bio: e.target.value})}
                placeholder="Présentez-vous en quelques lignes..."
                rows={4}
                className="mt-1"
              />
            </div>

            {/* Save button */}
            <div className="flex justify-end pt-4 border-t">
              <Button 
                onClick={handleSaveProfile} 
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Enregistrer les modifications
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Change password */}
        <ChangePasswordCard />
      </main>
    </div>
  );
};

export default TrainerProfilePage;
